/**
 * CDP SDK — a site's own consent choice (MEDIA-COMMERCE-2026-10-29 §1.3).
 *
 * Some sites keep the visitor's choice themselves, with no consent manager we know (market.dir.bg: its banner writes
 * `mw_consent` to localStorage, `necessary` = only the necessary cookies, and `window.mwConsent.get()` reads it). The
 * Site Profile names where the choice lives (`consent.bridge`); this module reads it on every page and whenever the
 * page may have changed it.
 *
 * The choice is applied for the page only — never written to `cdp_consent`. That cookie lives on the cookie domain
 * (`.dir.bg`), which the shop shares with the media: writing the shop's choice there would make it the media's too.
 */

import type { ConsentPreferences, SiteConsentBridge } from './types';
import { getCookie } from './storage';

const ALL: ConsentPreferences = { analytics: true, marketing: true, personalization: true, functional: true };
const NONE: ConsentPreferences = { analytics: false, marketing: false, personalization: false, functional: true };

function callGlobal(path: string): unknown {
  let target: unknown = window;
  let owner: unknown = window;
  for (const part of path.split('.')) {
    if (!target || (typeof target !== 'object' && typeof target !== 'function')) return undefined;
    owner = target;
    target = (target as Record<string, unknown>)[part];
  }
  return typeof target === 'function' ? (target as (this: unknown) => unknown).call(owner) : target;
}

function rawValue(bridge: SiteConsentBridge): unknown {
  if (bridge.global) {
    try {
      const v = callGlobal(bridge.global);
      if (v !== undefined && v !== null && v !== '') return v;
    } catch {
      /* the site's own function failing must not stop the storage read */
    }
  }
  if (bridge.storageKey) {
    try {
      const v = window.localStorage.getItem(bridge.storageKey);
      if (v !== null && v !== '') return v;
    } catch {
      /* storage blocked */
    }
    const c = getCookie(bridge.storageKey);
    if (c) return c;
  }
  return undefined;
}

/** A stored string may be JSON (`"necessary"` or `{"analytics":true}`). */
function parse(v: unknown): unknown {
  if (typeof v !== 'string') return v;
  const t = v.trim();
  if (t.startsWith('{') || t.startsWith('"')) {
    try {
      return JSON.parse(t);
    } catch {
      return t;
    }
  }
  return t;
}

/** The visitor's choice on this site, or null when the site has none yet (its mode decides then). */
export function readConsentBridge(bridge: SiteConsentBridge | undefined): ConsentPreferences | null {
  if (!bridge || (!bridge.storageKey && !bridge.global)) return null;
  const v = parse(rawValue(bridge));
  if (v === undefined || v === null || v === '') return null;
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>;
    const keys = ['analytics', 'marketing', 'personalization'] as const;
    if (!keys.some((k) => typeof o[k] === 'boolean')) return null;
    return {
      functional: true,
      analytics: o.analytics === true,
      marketing: o.marketing === true,
      personalization: o.personalization === true,
    };
  }
  const value = String(v).toLowerCase();
  const deny = (bridge.deny || []).map((x) => x.toLowerCase());
  const grant = bridge.grant?.map((x) => x.toLowerCase());
  if (deny.includes(value)) return { ...NONE };
  if (!grant || grant.includes(value)) return { ...ALL };
  return null;
}

/**
 * Watch for a change of the choice: after a click anywhere (the site's banner buttons), on another tab's storage write,
 * and on SPA navigation. `onChange` gets the new choice only when it differs from the last one. Returns a disposer.
 */
export function watchConsentBridge(
  bridge: SiteConsentBridge | undefined,
  initial: ConsentPreferences | null,
  onChange: (consent: ConsentPreferences) => void
): () => void {
  if (!bridge || (!bridge.storageKey && !bridge.global)) return () => {};
  let last = initial ? JSON.stringify(initial) : '';
  let timer: ReturnType<typeof setTimeout> | null = null;
  const check = () => {
    timer = null;
    const c = readConsentBridge(bridge);
    if (!c) return;
    const key = JSON.stringify(c);
    if (key === last) return;
    last = key;
    onChange(c);
  };
  const soon = () => {
    if (!timer) timer = setTimeout(check, 400);
  };
  const onStorage = (e: StorageEvent) => {
    if (!e.key || e.key === bridge.storageKey) soon();
  };
  document.addEventListener('click', soon, true);
  window.addEventListener('storage', onStorage);
  window.addEventListener('popstate', soon);
  return () => {
    document.removeEventListener('click', soon, true);
    window.removeEventListener('storage', onStorage);
    window.removeEventListener('popstate', soon);
    if (timer) clearTimeout(timer);
  };
}
